import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Request } from './Request';

const AuthContext = createContext(null);

const USER_KEY = 'user';
const TOKEN_KEY = 'token';

// Authentication context provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistUser = useCallback((userData, token) => {
    if (userData) {
      const { password, ...sanitized } = userData;
      localStorage.setItem(USER_KEY, JSON.stringify(sanitized));
      setUser(sanitized);
    }
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
  }, []);

  useEffect(() => {
    const fetchUserProfile = async (id) => {
      try {
        const data = await Request(`/users/${id}`, 'GET');
        if (data && (data.user || data.addresses)) {
          persistUser(data.user || data, data.token);
        }
      } catch (err) {
        console.error('Failed to sync user profile:', err);
      }
    };

    try {
      const raw = localStorage.getItem(USER_KEY);
      if (raw) {
        const u = JSON.parse(raw);
        setUser(u);
        if (u && u.id) {
          fetchUserProfile(u.id);
        }
      }
    } catch (e) {
      console.error('Failed to parse user from storage', e);
    } finally {
      setLoading(false);
    }
  }, [persistUser]);

  const login = useCallback(async (credentials) => {
    const payload = await Request('/login', 'POST', credentials);
    if (payload) {
      const userData = payload.user || payload;
      const token = payload.token || userData.token;
      persistUser(userData, token);
      return userData;
    }
    return null;
  }, [persistUser]);

  const register = useCallback(async (data) => {
    const payload = await Request('/register', 'POST', data);
    if (payload) {
      const userData = payload.user || payload;
      const token = payload.token || userData.token;
      persistUser(userData, token);
      return userData;
    }
    return null;
  }, [persistUser]);

  const loginWithGoogle = useCallback(async (credential) => {
    if (!credential) throw new Error('Missing Google credential');
    const payload = await Request('/auth/google', 'POST', { credential });
    if (payload) {
      const userData = payload.user || payload;
      const token = payload.token || userData.token;
      persistUser(userData, token);
      return userData;
    }
    return null;
  }, [persistUser]);

  const logout = useCallback(() => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const updateProfile = useCallback((nextData) => {
    persistUser(nextData);
    return nextData;
  }, [persistUser]);

  const addAddress = useCallback(async (addressData) => {
    if (!user) throw new Error('Not authenticated');
    const payload = await Request(`/users/${user.id}/addresses`, 'POST', addressData);
    if (payload && (payload.user || payload.addresses)) {
      persistUser(payload.user || payload, payload.token);
    } else if (payload && payload.id) {
      const updatedUser = { ...user, addresses: [...(user.addresses || []), payload] };
      persistUser(updatedUser);
    }
    return payload;
  }, [user, persistUser]);

  const updateAddress = useCallback(async (addressId, addressData) => {
    if (!user) throw new Error('Not authenticated');
    const payload = await Request(`/users/${user.id}/addresses/${addressId}`, 'PUT', addressData);
    if (payload && (payload.user || payload.addresses)) {
      persistUser(payload.user || payload, payload.token);
    } else if (payload && payload.id) {
      const updatedUser = {
        ...user,
        addresses: (user.addresses || []).map((a) => (a.id === addressId ? payload : a)),
      };
      persistUser(updatedUser);
    }
    return payload;
  }, [user, persistUser]);

  const deleteAddress = useCallback(async (addressId) => {
    if (!user) throw new Error('Not authenticated');
    const payload = await Request(`/users/${user.id}/addresses/${addressId}`, 'DELETE');
    if (payload && (payload.user || payload.addresses)) {
      persistUser(payload.user || payload, payload.token);
    } else {
      const updatedUser = {
        ...user,
        addresses: (user.addresses || []).filter((a) => a.id !== addressId),
      };
      persistUser(updatedUser);
    }
  }, [user, persistUser]);

  const getAddresses = useCallback(() => {
    return user?.addresses || [];
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      logout,
      getAddresses,
      addAddress,
      updateAddress,
      deleteAddress,
      updateProfile,
      setUser: persistUser,
    }),
    [
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      logout,
      getAddresses,
      addAddress,
      updateAddress,
      deleteAddress,
      updateProfile,
      persistUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
